import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from './state/hooks'
import { cookies, CookieType } from './state/cookies'
import {
  BrowserRouter,
  Navigate,
  redirect,
  Route,
  Routes,
} from 'react-router-dom'
import { QueryParamProvider, StringParam, useQueryParam } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import Login from './features/auth/Login'
import AuthLayout from './layout/AuthLayout'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
  UseQueryLoaderLoadQueryOptions,
} from 'react-relay'
import {
  selectAuth,
  selectViewingUserId,
  setAuth,
  setUserKey,
  setViewingUserId,
} from './state/appSlice'
import HomeRoute from './pages/HomeRoute'
import { graphql } from 'relay-runtime'
import { AppQuery, AppQuery$variables } from './__generated__/AppQuery.graphql'
import HomeLoading from './pages/HomeLoading'
import { Demo } from './pages/Demo'
import { Insights } from './pages/Insights'
import { Layout } from './layout/Layout/Layout'
import { ROUTES } from './routes'
import { useFeatureFlag } from './hooks'
import { useFsIdentify } from './features/tracking/useFsIdentify'
import { identifyAndGroupUser, StoredAuthUser } from './analytics/sessionIdentity'
import { AdsSummary } from './pages/AdsSummary'
import { AdvancedInsights } from './pages/AdvancedInsights'
import { AdminPage } from './features/admin/AdminPage'
import { AccessDenied } from './features/admin/AccessDenied'

export enum AppSearchParams {
  LINK_LOGIN = 'linkLogin',
}

export const appQuery = graphql`
  query AppQuery($input: GetRequest!) {
    getUser(input: $input) {
      id
      superuser
      featureFlags
      accessibleBrands {
        id
        brandFeatureFlags {
          featureFlagId
        }
      }
      ...hooksUseUserRoleCheck
      ...HomeUser
      ...CreativeDrawerUser
      ...Layout_User
    }
  }
`;


export const AppContainer = () => {
  const [appQueryRef, loadAppQuery] = useQueryLoader<AppQuery>(appQuery)
  return (
    <Suspense fallback={<AppLoading />}>
      <App appQueryRef={appQueryRef} loadAppQuery={loadAppQuery} />
    </Suspense>
  )
}

export const AppLoading = () => <HomeLoading />

interface AppProps {
  appQueryRef?: PreloadedQuery<AppQuery> | null
  loadAppQuery: (
    variables: AppQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions | undefined
  ) => void
}

const App = ({ appQueryRef, loadAppQuery }: AppProps) => {
  const dispatch = useAppDispatch()
  const auth = useAppSelector(selectAuth)
  const viewingUserId = useAppSelector(selectViewingUserId)
  const [isFirstRender, setIsFirstRender] = useState(true)
  useEffect(() => {
    if (isFirstRender) {
      const authCookie = cookies.get(CookieType.AUTH)
      if (authCookie != null) {
        dispatch(setAuth(authCookie))
        // Restored session (no fresh Auth0 login) — identify/group still need
        // to fire so Segment sees this user, since loginSuccessCallback won't run.
        identifyAndGroupUser(authCookie as StoredAuthUser)
      }
      const viewingUserIdCookie = cookies.get(CookieType.VIEWING_USER)
      if (viewingUserIdCookie != null) {
        dispatch(setViewingUserId(viewingUserIdCookie))
      }
      setIsFirstRender(false)
    }
  }, [isFirstRender, dispatch, setIsFirstRender])

  useEffect(() => {
    if (viewingUserId != null) {
      loadAppQuery({ input: { id: viewingUserId } })
    }
  }, [viewingUserId, loadAppQuery])

  if (auth === undefined || viewingUserId === undefined) {
    return (
      <AuthLayout>
        <Login />
      </AuthLayout>
    )
  }

  if (appQueryRef == null) {
    return <AppLoading />
  }

  return (
    <>
      <BrowserRouter>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
          <AppView appQueryRef={appQueryRef} />
        </QueryParamProvider>
      </BrowserRouter>
    </>
  );
}

export default App

interface AppViewProps {
  appQueryRef: PreloadedQuery<AppQuery>
}
export const featureFlagsMap: { [id: string]: string } = {
  '4fe37109-3d67-4f70-ab1c-36f0dc6aeedf': 'DEMO',
  '6edcb535-0a7a-4bdf-8351-b4c7dd0f92b4': 'CIH',
  'd31efc1f-485f-4240-9df6-82c20d52a3ae': 'WORKFLOW',
  '9947fc9c-fe62-4b59-91c4-8be204ac8bbb': 'ADVANCED_INSIGHTS',
  '19160311-a760-4dc2-b821-fefadc14f461': 'TAGS',
  'ea002f43-a3f5-4f69-8eaf-0224e5211d1a': 'PURCHASES',
  '3478479f-ba7f-422d-ad71-89020cbd4b48': 'LEADS',
  'da7e622f-0161-4677-a69a-8911c7b8646f': 'HOOK_TEXT',
  'd5413649-233b-4b2e-81df-2cbc26377f4d': 'HEADLINE',
  '711425e4-7197-43cc-bec7-940ccb6bb9ae': 'CURRENT_MONTH_DATA',
  'd5af2357-e396-41eb-83f9-1ac280e77b1a': 'INSIGHTS_GRAPH',
  '0800d1e9-bb96-4f6a-927c-99c6ad6e8147': 'LABEL_TYPE_LIFT_GRAPH',
  '5749df9a-19d3-4113-8056-d249006b0829': 'CONTAINERS_GRAPH',
  '1c06f7a0-d4a2-4e0a-a68c-473d3b9853b5': 'METRIC_INSIGHT_GRAPH_TOGGLE',
  'ff4477d7-924c-458f-a969-fd38f1c5d402': 'DOMINANT_COLOR_LABEL_TYPE',
  '53e2bb0e-2267-4421-adb2-57e56a5560f4': 'PRIMARY_COLOR_TREEMAP',
  '157ed11c-8d9b-4092-a118-d0402ee186d1': 'TREEMAP_AD_PREVIEW',
};

const AppView = ({ appQueryRef }: AppViewProps) => {
  const query = usePreloadedQuery(appQuery, appQueryRef).getUser
  const dispatch = useAppDispatch()
  const [brandIds] = useQueryParam('brandId', StringParam);
  const brandIdsArray = brandIds?.split(",") || []
  // Brand-scoped flags follow the brand(s) currently being viewed, for
  // superusers and regular users alike.
  const brandFeatureNames: string[] = Array.from(
    new Set(query.accessibleBrands
      .filter(brand => brandIdsArray.includes(brand.id))
      .flatMap(brand =>
        brand.brandFeatureFlags?.map(brandFeatureFlag =>
          brandFeatureFlag?.featureFlagId ? featureFlagsMap[brandFeatureFlag.featureFlagId] : undefined
        )
      )
      .filter(name => name !== undefined))
    ) as string[];
  const insightsEnabled = useFeatureFlag('CIH', (query.featureFlags || []).concat(brandFeatureNames))
  const workflowEnabled = useFeatureFlag('WORKFLOW', (query.featureFlags || []).concat(brandFeatureNames))
  const advancedInsightsEnabled = useFeatureFlag(
    'ADVANCED_INSIGHTS',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  const tagsEnabled = useFeatureFlag(
    'TAGS',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  const purchasesEnabled = useFeatureFlag(
    'PURCHASES',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  const leadsEnabled = useFeatureFlag(
    'LEADS',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  const hookTextEnabled = useFeatureFlag(
    'HOOK_TEXT',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  const headlineEnabled = useFeatureFlag(
    'HEADLINE',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  const insightsGraphEnabled = useFeatureFlag(
    'INSIGHTS_GRAPH',
    (query.featureFlags || []).concat(brandFeatureNames)
  )
  
  const hasCurrentMonthData = useFeatureFlag(
    'CURRENT_MONTH_DATA',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  const demoEnabled = useFeatureFlag('DEMO', (query.featureFlags || []).concat(brandFeatureNames))

  const isSuperuser = query.superuser ?? false

  // Add new feature flag for label type lift graph
  const labelTypeLiftGraphEnabled = useFeatureFlag(
    'LABEL_TYPE_LIFT_GRAPH',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  const metricInsightGraphEnabled = useFeatureFlag(
    'METRIC_INSIGHT_GRAPH_TOGGLE',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  const containersGraphEnabled = useFeatureFlag(
    'CONTAINERS_GRAPH',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  const dominantColorLabelTypeEnabled = useFeatureFlag(
    'DOMINANT_COLOR_LABEL_TYPE',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  const primaryColorTreemapEnabled = useFeatureFlag(
    'PRIMARY_COLOR_TREEMAP',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  const treemapAdPreviewEnabled = useFeatureFlag(
    'TREEMAP_AD_PREVIEW',
    (query.featureFlags || []).concat(brandFeatureNames)
  )

  useEffect(() => {
    console.log('[App] insights drawer flags', {
      metricInsightGraphEnabled,
      insightsGraphEnabled,
      brandIdQuery: brandIds ?? '',
    })
  }, [metricInsightGraphEnabled, insightsGraphEnabled, brandIds])

  const redirectRoute = useMemo(() => {
    if (workflowEnabled) return ROUTES.workflow.path
    if (insightsEnabled) return ROUTES.insights.path
    if (demoEnabled) return ROUTES.demo.path
    return '/'
  }, [demoEnabled, insightsEnabled, workflowEnabled])

  useEffect(() => {
    dispatch(setUserKey(query))
  }, [query, dispatch])

  useFsIdentify(query.id)

  return (
    <>
      <Layout fragmentKey={query}>
        <Routes>
          {workflowEnabled && brandIdsArray.length == 1 && (
            <Route path={ROUTES.workflow.path} element={<HomeRoute />} />
          )}
          {demoEnabled && (
            <Route path={ROUTES.demo.path} element={<Demo />} />
          )}
          {insightsEnabled && (
            <>
              <Route path={ROUTES.insights.path} element={<Insights tagsEnabled={tagsEnabled} hookTextEnabled={hookTextEnabled} headlineEnabled={headlineEnabled} dominantColorLabelTypeEnabled={dominantColorLabelTypeEnabled} insightsGraphEnabled={insightsGraphEnabled} metricInsightGraphEnabled={metricInsightGraphEnabled} hasCurrentMonthData={hasCurrentMonthData} labelTypeLiftGraphEnabled={labelTypeLiftGraphEnabled} containersGraphEnabled={containersGraphEnabled} primaryColorTreemapEnabled={primaryColorTreemapEnabled} treemapAdPreviewEnabled={treemapAdPreviewEnabled} />} />
              <Route
                path={ROUTES.adsSummary.path}
                element={<AdsSummary tagsEnabled={tagsEnabled} purchasesEnabled={purchasesEnabled} leadsEnabled={leadsEnabled} hasCurrentMonthData={hasCurrentMonthData}/>}
              />
            </>
          )}
          {advancedInsightsEnabled && (
            <Route
              path={ROUTES.advancedInsights.path}
              element={<AdvancedInsights />}
            />
          )}

          <Route
            path={ROUTES.admin.path}
            element={isSuperuser ? <AdminPage /> : <AccessDenied />}
          />

          <Route path="*" element={<Navigate to={redirectRoute} />} />
        </Routes>
      </Layout>
    </>
  )
}
